'use client'

import React, { FC, ReactElement } from 'react'
import Link from 'next/link'
import styles from './Wallet.module.css'
import WalletReceive from './wallet-receive'
import WalletSend from './wallet-send'
import WalletSign from './wallet-sign'
import useWalletWarning from '@/utils/hooks/useWalletWarning'
import useMoonpayModal from '@/utils/hooks/useMoonpayModal'
import event from '@/utils/event'
import WalletDashboard from './wallet-dashboard'
import WalletItemsSection from './wallet-items-section'
import Modal from '@/components/modal-v2'
import SatoshiContext from '@/components/satoshis/context'
import LocalEscrowsContext from '@/components/local-escrows/context'
import UtxosCart from './wallet-utxos/utxos-cart'
import SplitPrompt from './split-prompt'

type Tab = 'my-inscriptions' | 'wallet-history' | 'brc20' | 'sns' | 'utxos'
const DEFAULT = 0
const RECEIVE = 1
const SEND = 2
const SIGN = 3
const MANAGE_UTXOS = 4

const WALLET_TABS_ID = 'wallet-tabs'

const WalletPage: FC<{ isMobile: boolean }> = (props): ReactElement => {
	const [open, setOpen] = React.useState(false)
	const [walletAction, setWalletAction] = React.useState(DEFAULT)

	// Ensure page starts at top on mount
	React.useEffect(() => {
		window.scrollTo(0, 0)
	}, [])
	const [openModal] = useMoonpayModal()

	const handleManageUtxos = React.useCallback(() => {
		event.emit('command', { id: 'manage-utxos' })
	}, [])

	const toggleReceive = React.useCallback(() => {
		setOpen(true)
		setWalletAction(walletAction !== RECEIVE ? RECEIVE : DEFAULT)
	}, [walletAction])

	// Send opens the send form. The UTXO manager only splits/consolidates back
	// to this wallet (no destination since #437), so it cannot withdraw BTC.
	const toggleSend = React.useCallback(() => {
		setOpen(true)
		setWalletAction(walletAction !== SEND ? SEND : DEFAULT)
	}, [walletAction])

	const toggleSign = React.useCallback(() => {
		setOpen(true)
		setWalletAction(walletAction !== SIGN ? SIGN : DEFAULT)
	}, [walletAction])

	useWalletWarning()

	React.useEffect(() => {
		const handleSuccess = (payload: any) => {
			if (payload.type === 'send' && payload.event === 'success') {
				setWalletAction(DEFAULT)
			}
		}

		event.listen('open-broadcast-modal', handleSuccess)

		return () => {
			event.removeListener('open-broadcast-modal', handleSuccess)
		}
	}, [])

	const handleCloseModal = () => {
		setOpen(false)
		setWalletAction(DEFAULT)
	}

	const openTab = (_tab: Tab, _ticker?: string) => {
		document.getElementById(WALLET_TABS_ID)?.scrollIntoView({
			behavior: 'smooth'
		})
	}

	return (
		<SatoshiContext>
			<LocalEscrowsContext>
				<div className={styles.root}>
					{/* Dashboard Header */}
					<WalletDashboard
						openTab={openTab}
						toggleSignMessage={toggleSign}
						toggleSendCoin={toggleSend}
						toggleRecieveCoin={toggleReceive}
						toggleTopupModal={openModal}
						toggleManageUtxos={handleManageUtxos}
					/>
					{/* New Items Section */}
					<div id={WALLET_TABS_ID}>
						<WalletItemsSection isMobile={props.isMobile} />
					</div>

					<SplitPrompt />

					{/* Legacy wallet fallback link */}
					<div className={styles.legacyLink}>
						<Link href="/legacy-wallet">Switch to legacy wallet</Link>
					</div>
				</div>
				<Modal open={open} onClose={handleCloseModal} bodyClassName={styles.modal} ariaLabel={walletAction === RECEIVE ? 'Receive or swap to Bitcoin' : undefined}>
					{walletAction === SEND && <WalletSend handleModalClose={handleCloseModal} />}
					{walletAction === RECEIVE && <WalletReceive handleModalClose={handleCloseModal} />}
					{walletAction === SIGN && <WalletSign handleModalClose={handleCloseModal} />}
					{walletAction === MANAGE_UTXOS && <UtxosCart handleModalClose={handleCloseModal} />}
				</Modal>
			</LocalEscrowsContext>
		</SatoshiContext>
	)
}

export default WalletPage
